import React, { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { api, formatApiError } from '../lib/api';
import { Loader2, Eye, EyeOff, CheckCircle2, Mail } from 'lucide-react';
import { BrandLogo } from '../components/BrandLogo';

function Shell({ children, title, subtitle }) {
  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10 bg-surface">
      <div className="w-full max-w-md surface border border-soft rounded-xl shadow p-8">
        <div className="flex items-center gap-3 mb-6">
          <BrandLogo />
        </div>
        <h1 className="text-2xl font-heading font-bold tracking-tight">{title}</h1>
        {subtitle && <p className="text-sm text-fg-muted mt-2">{subtitle}</p>}
        <div className="mt-6">{children}</div>
      </div>
    </div>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    setErr(''); setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email });
      setSubmitted(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || formatApiError(e));
    } finally { setLoading(false); }
  };
  if (submitted) {
    return (
      <Shell title="Check your inbox" subtitle="If that email is on file, we just sent a password-reset link. It expires in 60 minutes.">
        <CheckCircle2 className="w-10 h-10 text-brand mx-auto"/>
        <p className="text-center text-sm text-fg-muted mt-3">Didn't receive it? Check spam, or <button onClick={()=>setSubmitted(false)} className="text-brand underline">try a different email</button>.</p>
        <div className="mt-6 text-center text-sm">
          <Link to="/login" className="text-brand">← Back to sign in</Link>
        </div>
      </Shell>
    );
  }
  return (
    <Shell title="Reset your password" subtitle="Enter your email and we'll send you a secure reset link.">
      <form onSubmit={submit} className="space-y-4">
        <label className="block">
          <div className="text-xs text-fg-muted mb-1">Email</div>
          <input type="email" required className="input" value={email}
                 onChange={e=>setEmail(e.target.value)} data-testid="forgot-email" autoFocus/>
        </label>
        {err && <div className="text-sm text-[#B83A3A]">{err}</div>}
        <button type="submit" disabled={loading || !email}
                className="btn-primary w-full inline-flex items-center justify-center gap-2"
                data-testid="forgot-submit">
          {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : <Mail className="w-4 h-4"/>}
          Send reset link
        </button>
        <div className="text-center text-sm">
          <Link to="/login" className="text-fg-muted hover:text-brand">← Back to sign in</Link>
        </div>
      </form>
    </Shell>
  );
}

export function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (pw !== pw2) { setErr('Passwords do not match.'); return; }
    if (pw.length < 8) { setErr('Use at least 8 characters.'); return; }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, new_password: pw });
      setDone(true);
      setTimeout(() => navigate('/login'), 2200);
    } catch (e) {
      setErr(e?.response?.data?.detail || formatApiError(e));
    } finally { setLoading(false); }
  };
  if (done) {
    return (
      <Shell title="Password updated" subtitle="Redirecting you to sign in…">
        <CheckCircle2 className="w-10 h-10 text-brand mx-auto"/>
      </Shell>
    );
  }
  return (
    <Shell title="Choose a new password" subtitle="Use ≥8 characters with upper, lower and a digit.">
      <form onSubmit={submit} className="space-y-4">
        <label className="block">
          <div className="text-xs text-fg-muted mb-1">New password</div>
          <div className="relative">
            <input type={show ? 'text' : 'password'} required className="input pr-10"
                   value={pw} onChange={e=>setPw(e.target.value)} data-testid="reset-pw" autoFocus/>
            <button type="button" onClick={()=>setShow(v=>!v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-fg-muted hover:text-fg" data-testid="reset-toggle">
              {show ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
            </button>
          </div>
        </label>
        <label className="block">
          <div className="text-xs text-fg-muted mb-1">Confirm new password</div>
          <input type={show ? 'text' : 'password'} required className="input"
                 value={pw2} onChange={e=>setPw2(e.target.value)} data-testid="reset-pw2"/>
        </label>
        {err && <div className="text-sm text-[#B83A3A]" data-testid="reset-err">{err}</div>}
        <button type="submit" disabled={loading || !pw || !pw2}
                className="btn-primary w-full inline-flex items-center justify-center gap-2"
                data-testid="reset-submit">
          {loading && <Loader2 className="w-4 h-4 animate-spin"/>}
          Set new password
        </button>
      </form>
    </Shell>
  );
}
